// utils/LiveTrackingMap.jsx
//
// FIXES applied
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// 1. useSocket() now destructures socketRef (stable React ref) in addition to
//    socket and connected. socketRef is passed to startTracking() so the tracker
//    always reads the live socket, even after a reconnect.
//
// 2. startTracking(callback, socketRef, userId) â€” was passing `socket` (raw,
//    potentially stale). Now passes socketRef so safeEmit() in Locationtracker
//    reads socketRef.current on every emit.
//
// 3. All other logic (geofence, drawing manager, trail, markers) is unchanged.

import { useEffect, useRef, useState, useCallback } from "react";
import {
  startTracking,
  stopTracking,
  getTrackPoints,
  isCurrentlyTracking,
} from "./Locationtracker";
import { useSocket } from "./Usesocket.js";
import { toast } from "../components/useToast.jsx";

const API  = "https://solar-backend-2-r6k9.onrender.com";
const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyBItE5PzCbzz2cVJVxtPy0MEoTdzbFEdxA";

const MIN_MOVE_METRES = 12;

const addressCache = new Map();

async function fetchFallbackAddress(lat, lng) {
  const numLat = Number(lat);
  const numLng = Number(lng);
  if (!Number.isFinite(numLat) || !Number.isFinite(numLng)) return "";

  const key = `${numLat.toFixed(5)},${numLng.toFixed(5)}`;
  if (addressCache.has(key)) return addressCache.get(key);

  let address = "";

  // 1. Try OpenStreetMap Nominatim
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${numLat}&lon=${numLng}&format=json`
    );
    const data = await res.json();
    if (data) {
      const addr = data.address || {};
      const street = addr.road || addr.pedestrian || addr.street || "";
      const area = addr.suburb || addr.neighbourhood || addr.quarter || addr.residential || addr.village || "";
      const city = addr.city || addr.town || addr.county || addr.state_district || "";
      const state = addr.state || "";
      const postcode = addr.postcode || "";

      const locationParts = [street, area, city, state, postcode].filter(Boolean);
      if (locationParts.length >= 2) {
        address = locationParts.join(", ");
      } else if (data.display_name) {
        address = data.display_name;
      }
    }
  } catch (e) {}

  // 2. Fallback: BigDataCloud API
  if (!address) {
    try {
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${numLat}&longitude=${numLng}&localityLanguage=en`
      );
      const data = await res.json();
      if (data) {
        const parts = [
          data.locality || data.city || data.name,
          data.principalSubdivision || data.state,
          data.countryName,
        ].filter(Boolean);
        if (parts.length > 0) address = parts.join(", ");
      }
    } catch (e) {}
  }

  if (address) {
    addressCache.set(key, address);
  }
  return address;
}

const looksLikeCoordinateText = (value = "") => {
  const text = String(value).trim();
  return /^-?\d{1,2}(?:\.\d+)?\s*,\s*-?\d{1,3}(?:\.\d+)?$/.test(text);
};

const formatPopupAddress = (address) => {
  if (typeof address === "string") {
    const text = address.trim();
    return looksLikeCoordinateText(text) ? "" : text;
  }
  const text = (
    address?.full ||
    address?.short ||
    address?.formattedAddress ||
    ""
  ).trim();
  return looksLikeCoordinateText(text) ? "" : text;
};

const buildVisitInfoHtml = (visit, label, lat, lng, statusColor, isInProgress, addressText = "") => {
  const cleanAddress = formatPopupAddress(addressText || visit?.address);
  const timeText = visit?.checkInTime
    ? new Date(visit.checkInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : "";

  return `
    <div style="font-family:'Inter',sans-serif;padding:8px 10px;min-width:190px;max-width:280px">
      <div style="font-weight:700;font-size:13px;color:#0f172a;margin-bottom:6px">
        ${escapeHtml(label)}
      </div>
      <div style="font-size:12px;color:#334155;font-weight:600;line-height:1.4;margin-bottom:6px">
        ${cleanAddress ? escapeHtml(cleanAddress) : "Resolving address..."}
      </div>
      <div style="font-size:11px;color:#64748b;margin-bottom:4px">
        ${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}
      </div>
      ${timeText
        ? `<div style="font-size:11px;color:#94a3b8">${escapeHtml(timeText)}</div>`
        : ""}
      <div style="margin-top:7px;display:inline-block;padding:2px 8px;border-radius:999px;font-size:10px;font-weight:700;
            background:${isInProgress ? "rgba(34,197,94,0.1)" : "rgba(69,105,234,0.1)"};
            color:${statusColor}">
        ${escapeHtml(isInProgress ? "In Progress" : visit?.status || "Visited")}
      </div>
    </div>`;
};

const openVisitInfoWindow = async (infoWindow, map, marker, visit, label, lat, lng, statusColor, isInProgress) => {
  const initialAddress = formatPopupAddress(visit?.address);
  infoWindow.setContent(buildVisitInfoHtml(visit, label, lat, lng, statusColor, isInProgress, initialAddress));
  infoWindow.open(map, marker);

  if (initialAddress) return;

  const resolvedAddress = await fetchFallbackAddress(lat, lng);
  if (resolvedAddress) {
    infoWindow.setContent(buildVisitInfoHtml(visit, label, lat, lng, statusColor, isInProgress, resolvedAddress));
  }
};
const SNAP_TO_ROADS_MAX_POINTS = 100;
const SNAP_TO_ROADS_MAX_GAP_METRES = 500;
const MAX_COUNTED_SEGMENT_METRES = 1000;
const MAX_COUNTED_SPEED_KMH = 100;
const ROUTE_MODE_RAW = "raw";
const ROUTE_MODE_ROAD = "road";
const DRAW_MODE_CIRCLE = "circle";
const DRAW_MODE_POLYGON = "polygon";
const MAX_VISIBLE_GPS_DOTS = 1000;
const LIVE_MARKER_MIN_MOVE_METRES = 8;
const LIVE_MARKER_FORCE_MOVE_METRES = 35;
const LIVE_MARKER_MAX_ACCURACY_METRES = 150;
const LIVE_MARKER_ANIMATION_MS = 700;

function getGpsNoiseRadiusMetres(currentAccuracy = 0, previousAccuracy = 0) {
  const current = Number(currentAccuracy) || 0;
  const previous = Number(previousAccuracy) || 0;
  return Math.max(12, Math.max(current, previous) * 0.8);
}

function getEffectiveMoveThreshold(currentAccuracy = 0, previousAccuracy = 0) {
  const current = Number(currentAccuracy) || 0;
  const previous = Number(previousAccuracy) || 0;
  const maxAccuracy = Math.max(current, previous);

  return Math.min(
    45,
    Math.max(
      MIN_MOVE_METRES,
      (current + previous) * 0.45,
      maxAccuracy * 0.8
    )
  );
}

const getToken = () =>
  localStorage.getItem("token")       ||
  localStorage.getItem("authToken")   ||
  localStorage.getItem("accessToken") || "";

const getLocalDateString = (value = null) => {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return new Date().toISOString().split("T")[0];
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const extractLocationPoints = (payload) => {
  const candidates = [
    payload?.result,
    payload?.data,
    payload?.points,
    payload?.path,
    payload?.locations,
    payload?.result?.points,
    payload?.result?.path,
    payload?.result?.locations,
    payload?.data?.points,
    payload?.data?.path,
    payload?.data?.locations,
  ];

  return candidates.find(Array.isArray) || [];
};

const filterPointsFromPunchInTime = (points = [], punchInTime = null) => {
  if (!Array.isArray(points) || !points.length || !punchInTime) return points;

  const punchInMs = new Date(punchInTime).getTime();
  if (!Number.isFinite(punchInMs)) return points;

  return points.filter((point) => {
    const pointMs = new Date(point?.recordedAt || point?.time || 0).getTime();
    return !Number.isFinite(pointMs) || pointMs >= punchInMs;
  });
};

const getLocationPoint = (item) => {
  const lat = Number(
    item?.coordinates?.lat ??
      item?.location?.lat ??
      item?.location?.latitude ??
      item?.lat ??
      item?.latitude
  );
  const lng = Number(
    item?.coordinates?.lng ??
      item?.coordinates?.longitude ??
      item?.location?.lng ??
      item?.location?.longitude ??
      item?.lng ??
      item?.longitude
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return {
    ...item,
    lat,
    lng,
    recordedAt:
      item?.recordedAt ||
      item?.timestamp ||
      item?.time ||
      item?.checkOutTime ||
      item?.endTime ||
      item?.lastEndTime ||
      item?.checkInTime ||
      item?.startTime ||
      item?.createdAt ||
      null,
  };
};

const getLocationPointTime = (point) => {
  const timestamp = new Date(
    point?.recordedAt ||
      point?.timestamp ||
      point?.time ||
      point?.checkOutTime ||
      point?.endTime ||
      point?.lastEndTime ||
      point?.checkInTime ||
      point?.startTime ||
      point?.createdAt ||
      0
  ).getTime();
  return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : null;
};

const pickLatestLocationPoint = (points = []) => {
  const normalized = points.map(getLocationPoint).filter(Boolean);
  if (!normalized.length) return null;

  const withTimes = normalized
    .map((point, index) => ({ point, index, time: getLocationPointTime(point) }))
    .filter((entry) => entry.time !== null);

  if (withTimes.length) {
    withTimes.sort((a, b) => (b.time - a.time) || (b.index - a.index));
    return withTimes[0].point;
  }

  return normalized[0];
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getPointAddressText = (point) => {
  const address = point?.address;
  if (typeof address === "string" && address.trim()) return address.trim();
  return (
    address?.full ||
    address?.short ||
    address?.formattedAddress ||
    point?.formattedAddress ||
    point?.locationName ||
    ""
  );
};

const extractVisitTrailPoints = (visits = [], punchInLocation = null) => {
  const points = [];
  const seen = new Set();

  const pushPoint = (latValue, lngValue, extra = {}) => {
    const lat = Number(latValue);
    const lng = Number(lngValue);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({ lat, lng, ...extra });
  };

  if (punchInLocation) {
    pushPoint(punchInLocation.lat, punchInLocation.lng, { source: "punch-in" });
  }

  [...visits]
    .filter(Boolean)
    .sort((a, b) => {
      const timeA = new Date(a?.checkInTime || a?.createdAt || a?.visitDate || 0).getTime();
      const timeB = new Date(b?.checkInTime || b?.createdAt || b?.visitDate || 0).getTime();
      return timeA - timeB;
    })
    .forEach((visit) => {
      pushPoint(
        visit.coordinates?.lat ??
          visit.location?.lat ??
          visit.location?.latitude ??
          visit.lat ??
          visit.latitude,
        visit.coordinates?.lng ??
          visit.coordinates?.longitude ??
          visit.location?.lng ??
          visit.location?.longitude ??
          visit.lng ??
          visit.longitude,
        { source: "visit" }
      );
    });

  return points;
};

const ensureTrailStartsAtPunchIn = (points = [], punchInLocation = null) => {
  if (!punchInLocation?.lat || !punchInLocation?.lng) return points;
  const punchPoint = {
    lat: Number(punchInLocation.lat),
    lng: Number(punchInLocation.lng),
    accuracy: Number(punchInLocation.accuracy ?? 0),
    source: "punch-in",
  };

  if (!Number.isFinite(punchPoint.lat) || !Number.isFinite(punchPoint.lng)) {
    return points;
  }

  const first = points[0];
  if (
    first &&
    Math.abs(Number(first.lat) - punchPoint.lat) < 0.00001 &&
    Math.abs(Number(first.lng) - punchPoint.lng) < 0.00001
  ) {
    return points;
  }

  return [punchPoint, ...points];
};

function appendSequentialPoint(points, point, minDuplicateMetres = 2) {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return points;

  const normalized = {
    ...point,
    lat,
    lng,
    accuracy: Number(point?.accuracy ?? 0),
  };
  const last = points[points.length - 1];
  if (last && distMetres(last.lat, last.lng, lat, lng) < minDuplicateMetres) {
    return points;
  }

  points.push(normalized);
  return points;
}

function getPointTimestamp(point) {
  const timestamp = new Date(point?.recordedAt || point?.time || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function orderPointsByTime(points = []) {
  return points
    .map((point, index) => ({ point, index, timestamp: getPointTimestamp(point) }))
    .sort((a, b) => {
      if (a.timestamp !== null && b.timestamp !== null && a.timestamp !== b.timestamp) {
        return a.timestamp - b.timestamp;
      }
      if (a.timestamp !== null && b.timestamp === null) return -1;
      if (a.timestamp === null && b.timestamp !== null) return 1;
      return a.index - b.index;
    })
    .map(({ point }) => point);
}

function calcKm(points) {
  if (!points || points.length < 2) return 0;
  const R = 6371;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const dLat = ((curr.lat - prev.lat) * Math.PI) / 180;
    const dLng = ((curr.lng - prev.lng) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((prev.lat * Math.PI) / 180) *
      Math.cos((curr.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return Math.round(total * 1000) / 1000;
}

function calcFilteredKm(points) {
  if (!points || points.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const distM = distMetres(prev.lat, prev.lng, curr.lat, curr.lng);
    const prevTime = new Date(prev.recordedAt || prev.time || 0).getTime();
    const currTime = new Date(curr.recordedAt || curr.time || 0).getTime();
    const elapsedHours =
      Number.isFinite(prevTime) && Number.isFinite(currTime) && currTime > prevTime
        ? (currTime - prevTime) / 3600000
        : null;
    const maxBySpeed = elapsedHours
      ? Math.max(150, elapsedHours * MAX_COUNTED_SPEED_KMH * 1000)
      : MAX_COUNTED_SEGMENT_METRES;
    const requiredMove = getEffectiveMoveThreshold(curr.accuracy, prev.accuracy);
    const gpsNoiseRadius = getGpsNoiseRadiusMetres(curr.accuracy, prev.accuracy);
    if (distM > Math.min(MAX_COUNTED_SEGMENT_METRES, maxBySpeed)) continue;
    if (distM >= requiredMove && distM >= gpsNoiseRadius) total += distM / 1000;
  }
  return Math.round(total * 1000) / 1000;
}

function calcTrustedKm(points, roadPath = [], hasMovedOverride = null) {
  const hasMoved =
    typeof hasMovedOverride === "boolean"
      ? hasMovedOverride
      : hasConfirmedMovement(points);

  if (!hasMoved) return 0;

  const filteredKm = calcFilteredKm(points);
  if (filteredKm > 0) return filteredKm;

  const roadKm = Array.isArray(roadPath) && roadPath.length >= 2 ? calcKm(roadPath) : 0;
  return roadKm > 0 ? roadKm : 0;
}

function hasConfirmedMovement(points) {
  if (!Array.isArray(points) || points.length < 2) return false;

   let significantSegments = 0;
   let travelledMetres = 0;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const distM = distMetres(prev.lat, prev.lng, curr.lat, curr.lng);
    const minMoveRequired = getEffectiveMoveThreshold(curr.accuracy, prev.accuracy);
    const gpsNoiseRadius = getGpsNoiseRadiusMetres(curr.accuracy, prev.accuracy);
    if (distM >= minMoveRequired && distM >= gpsNoiseRadius) {
      significantSegments += 1;
      travelledMetres += distM;
    }
  }

  return significantSegments >= 2 || travelledMetres >= 80;
}

function distMetres(lat1, lng1, lat2, lng2) {
  return calcKm([{ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 }]) * 1000;
}

function resolvePunchInMarkerPosition(punchInLocation, trailPoints = []) {
  if (!punchInLocation?.lat || !punchInLocation?.lng) return null;

  const trailStart = Array.isArray(trailPoints) && trailPoints.length > 0 ? trailPoints[0] : null;
  if (!trailStart?.lat || !trailStart?.lng) return punchInLocation;

  const mismatchMetres = distMetres(
    punchInLocation.lat,
    punchInLocation.lng,
    trailStart.lat,
    trailStart.lng
  );

  // If the saved punch-in point is clearly off, prefer the first confirmed trail point.
  if (mismatchMetres >= 60) {
    return {
      ...punchInLocation,
      lat: trailStart.lat,
      lng: trailStart.lng,
    };
  }

  return punchInLocation;
}

let gmapsPromise = null;
function loadGoogleMaps(retries = 2) {
  if (window.google?.maps?.Map) {
    return Promise.resolve();
  }
  if (gmapsPromise) {
    return gmapsPromise;
  }

  gmapsPromise = new Promise((resolve, reject) => {
    const isReady = () => Boolean(window.google?.maps?.Map);
    if (isReady()) {
      resolve();
      return;
    }

    const scriptId = "google-maps-js-sdk";
    let script = document.getElementById(scriptId);

    const checkReadyInterval = setInterval(() => {
      if (isReady()) {
        clearInterval(checkReadyInterval);
        clearTimeout(timeoutId);
        resolve();
      }
    }, 150);

    const timeoutId = setTimeout(() => {
      clearInterval(checkReadyInterval);
      if (isReady()) {
        resolve();
      } else {
        gmapsPromise = null;
        reject(new Error("Google Maps script load timed out. Check your internet connection or API key."));
      }
    }, 12000);

    const onScriptLoaded = () => {
      const waitMap = setInterval(() => {
        if (isReady()) {
          clearInterval(waitMap);
          clearTimeout(timeoutId);
          clearInterval(checkReadyInterval);
          resolve();
        }
      }, 50);
      setTimeout(() => {
        clearInterval(waitMap);
        if (isReady()) resolve();
      }, 3000);
    };

    const onScriptError = () => {
      clearTimeout(timeoutId);
      clearInterval(checkReadyInterval);
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      gmapsPromise = null;
      if (retries > 0) {
        console.warn(`[Google Maps] Load error. Retrying... (${retries} attempts left)`);
        setTimeout(() => {
          loadGoogleMaps(retries - 1).then(resolve).catch(reject);
        }, 1500);
      } else {
        reject(new Error("Failed to load Google Maps script. Check network or firewall/VPN settings."));
      }
    };

    if (script) {
      script.addEventListener("load", onScriptLoaded, { once: true });
      script.addEventListener("error", onScriptError, { once: true });
    } else {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GKEY}&libraries=places,geometry,drawing`;
      script.async = true;
      script.onload = onScriptLoaded;
      script.onerror = onScriptError;
      document.head.appendChild(script);
    }
  });

  return gmapsPromise;
}

export default function LiveTrackingMap({
  isPunchedIn     = false,
  hasPunchedOut   = false,
  userId          = null,
  height          = "400px",
  locateTrigger   = 0,
  onPointsChange,
  onDistanceChange,
  isOwner         = true,
  punchInLocation = null,
  punchOutLocation = null,
  currentLocationPoint = null,
  selectedDate    = null, 
  visits          = [],
  preferVisitTrail = false,
  
}) {
  const mapDivRef  = useRef(null);
  const gMapRef    = useRef(null);
  const polyRef    = useRef({ casing: null, main: null });
  const startMkRef = useRef(null);
  const liveMkRef  = useRef(null);
  const liveInfoWindowRef = useRef(null);
  const liveGeocoderRef = useRef(null);
  const liveMarkerClickRef = useRef(null);
  const liveMarkerSmoothedRef = useRef(null);
  const liveMarkerAnimationRef = useRef(null);
  const endMkRef   = useRef(null);
  const dotsRef    = useRef([]);
  const heatmapRouteRef = useRef({ casings: [], lines: [], labels: [] });
  const heatmapPathRef = useRef([]);
  const heatmapZoomTimerRef = useRef(null);
  const routeModeRef = useRef(ROUTE_MODE_ROAD);
  const allPtsRef  = useRef([]);
  const rawPtsRef  = useRef([]);
  const visitsRef = useRef(visits);
  const punchInLocationRef = useRef(punchInLocation);
  const punchOutLocationRef = useRef(punchOutLocation);
  const currentLocationPointRef = useRef(currentLocationPoint);
  const snappedTrailCacheRef = useRef({ key: "", path: [] });
  const drawRequestSeqRef = useRef(0);
const visitMkRef = useRef([]);
  const userAdjustedViewportRef = useRef(false);
  const suppressViewportEventsRef = useRef(false);
  const [mapLoaded,  setMapLoaded]  = useState(false);
  const [mapError,   setMapError]   = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const mapOuterContainerRef = useRef(null);

  const toggleFullScreen = useCallback(() => {
    const el = mapOuterContainerRef.current;
    if (!el) return;

    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        el.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  }, []);

  useEffect(() => {
    const handleFSChange = () => {
      const isFS = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullScreen(isFS);
      setTimeout(() => {
        if (gMapRef.current && window.google?.maps?.event) {
          window.google.maps.event.trigger(gMapRef.current, "resize");
        }
      }, 150);
    };

    document.addEventListener("fullscreenchange", handleFSChange);
    document.addEventListener("webkitfullscreenchange", handleFSChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFSChange);
      document.removeEventListener("webkitfullscreenchange", handleFSChange);
    };
  }, []);
  const [accuracy,   setAccuracy]   = useState(null);
  const [sockAck,    setSockAck]    = useState(null);
  const [totalKm,    setTotalKm]    = useState(0);
  const [pointCount, setPointCount] = useState(0);
  const [gpsDotCount, setGpsDotCount] = useState(0);
  const [routeMode, setRouteMode] = useState(ROUTE_MODE_ROAD);
  const [roadTravel, setRoadTravel] = useState({
    totalKm: null,
    segmentCount: 0,
    source: null,
    status: null,
    quality: null,
    confidence: null,
    mapboxConfigured: null,
    loading: false,
  });
  const [drawMode, setDrawMode] = useState(null);

  const prevPunchedIn  = useRef(false);
  const prevPunchedOut = useRef(false);

  // âœ… FIX: destructure socketRef â€” a stable ref that always points to the live socket
  const { socket, socketRef, connected } = useSocket();

  const [geofences,      setGeofences]      = useState([]);
  const [geofenceAlerts, setGeofenceAlerts] = useState([]);
  const fenceLayersRef = useRef({});
  const draftFenceRef = useRef({ overlay: null, markers: [], path: [] });

  const clearDraftFence = useCallback(() => {
    const draft = draftFenceRef.current;
    draft.overlay?.setMap(null);
    draft.markers.forEach((marker) => marker.setMap(null));
    draftFenceRef.current = { overlay: null, markers: [], path: [] };
  }, []);

  const saveGeofence = useCallback(async (payload, overlay) => {
    try {
      const res = await fetch(`${API}/api/v1/location/geofences`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to save geofence.");

      fenceLayersRef.current[data.data._id] = overlay;
      setGeofences((prev) => [...prev, data.data]);
      toast.success(`Geofence "${payload.name}" saved.`);
    } catch (error) {
      overlay?.setMap(null);
      toast.error(error.message || "Failed to save geofence.");
    }
  }, []);

  const finishPolygonGeofence = useCallback(async () => {
    const draft = draftFenceRef.current;
    if (!draft.overlay || draft.path.length < 3) {
      toast.error("Add at least 3 points to save a polygon geofence.");
      return;
    }

    const name = window.prompt("Name this geofence:");
    if (!name) return;

    const polygon = draft.overlay;
    const coordinates = polygon.getPath().getArray().map((ll) => ({
      lat: ll.lat(),
      lng: ll.lng(),
    }));

    draft.markers.forEach((marker) => marker.setMap(null));
    draftFenceRef.current = { overlay: null, markers: [], path: [] };
    setDrawMode(null);

    await saveGeofence({ name, type: "polygon", coordinates }, polygon);
  }, [saveGeofence]);

  const syncStartMarker = useCallback((points = allPtsRef.current) => {
    if (!mapLoaded) return;

    const resolvedPunchIn = resolvePunchInMarkerPosition(
      punchInLocationRef.current,
      points
    );

    if (!resolvedPunchIn?.lat || !resolvedPunchIn?.lng) {
      if (startMkRef.current) {
        startMkRef.current.setMap(null);
        startMkRef.current = null;
      }
      return;
    }

    if (!gMapRef.current || !window.google?.maps) return;
    const G = window.google.maps;
    const map = gMapRef.current;
    const pos = { lat: resolvedPunchIn.lat, lng: resolvedPunchIn.lng };

    if (!userAdjustedViewportRef.current) {
      withProgrammaticViewport(() => {
        map.setCenter(pos);
        map.setZoom(15);
      });
    }

    if (!startMkRef.current) {
      startMkRef.current = new G.Marker({
        position: pos,
        map,
        title: "Punch-in Location",
        zIndex: 10,
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#2563eb",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
      });
    } else {
      startMkRef.current.setPosition(pos);
    }
  }, [mapLoaded]);

  const isTodaySelected =
    !selectedDate || selectedDate === new Date().toISOString().split("T")[0];
  const isLive = isPunchedIn && !hasPunchedOut && isTodaySelected;

  const updateDistanceStats = useCallback((km, points = 0, extra = {}) => {
    const normalizedKm = Number.isFinite(Number(km)) ? Number(km) : 0;
    const normalizedPoints = Number.isFinite(Number(points)) ? Number(points) : 0;
    setTotalKm(normalizedKm);
    setPointCount(normalizedPoints);
    if (typeof onDistanceChange === "function") {
      onDistanceChange({ totalKm: normalizedKm, totalPoints: normalizedPoints, ...extra });
    }
  }, [onDistanceChange]);

  const withProgrammaticViewport = useCallback((fn) => {
    suppressViewportEventsRef.current = true;
    fn();
    window.setTimeout(() => {
      suppressViewportEventsRef.current = false;
    }, 350);
  }, []);

  const getResolvedCurrentLocationPoint = useCallback((includeMarker = false) => {
    const markerPosition = liveMkRef.current?.getPosition?.();
    const markerPoint = includeMarker && markerPosition
      ? { lat: markerPosition.lat(), lng: markerPosition.lng() }
      : null;
    const latestRawPoint = rawPtsRef.current[rawPtsRef.current.length - 1];
    const latestVisitPoint = pickLatestLocationPoint(visitsRef.current);

    return pickLatestLocationPoint([
      currentLocationPointRef.current,
      latestRawPoint,
      markerPoint,
      latestVisitPoint,
    ]);
  }, []);

  const buildLiveLocationInfoHtml = useCallback((point, addressText = "") => {
    const current = getLocationPoint(point);
    const lat = Number(current?.lat);
    const lng = Number(current?.lng);
    const coords = Number.isFinite(lat) && Number.isFinite(lng)
      ? `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      : "";
    const accuracy = Number(current?.accuracy);
    const timeValue = current?.recordedAt || current?.time || current?.timestamp || null;
    const time = timeValue ? new Date(timeValue).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "";

    return `
      <div style="font-family:Inter,Arial,sans-serif;padding:10px 12px;max-width:260px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="width:10px;height:10px;border-radius:999px;background:#ef4444;box-shadow:0 0 0 4px rgba(239,68,68,0.14);display:inline-block"></span>
          <div style="font-size:13px;font-weight:800;color:#111827">Current Location</div>
        </div>
        <div style="font-size:12px;line-height:1.45;color:#475569;margin-bottom:6px">
          ${addressText ? escapeHtml(addressText) : "Finding address..."}
        </div>
        ${coords ? `<div style="font-size:11px;color:#94a3b8;margin-bottom:4px">${escapeHtml(coords)}</div>` : ""}
        ${Number.isFinite(accuracy) && accuracy > 0 ? `<div style="font-size:11px;color:#94a3b8">Accuracy +/-${Math.round(accuracy)} m${time ? ` Â· ${escapeHtml(time)}` : ""}</div>` : time ? `<div style="font-size:11px;color:#94a3b8">${escapeHtml(time)}</div>` : ""}
      </div>`;
  }, []);

  const openLiveLocationInfo = useCallback(() => {
    if (!gMapRef.current || !liveMkRef.current || !window.google?.maps) return;

    const G = window.google.maps;
    const point = getResolvedCurrentLocationPoint(true);
    if (!point) return;

    const fallbackAddress = getPointAddressText(point);
    if (!liveInfoWindowRef.current) {
      liveInfoWindowRef.current = new G.InfoWindow();
    }
    liveInfoWindowRef.current.setContent(buildLiveLocationInfoHtml(point, fallbackAddress));
    liveInfoWindowRef.current.open(gMapRef.current, liveMkRef.current);

    if (fallbackAddress) return;

    if (!liveGeocoderRef.current) {
      liveGeocoderRef.current = new G.Geocoder();
    }
    liveGeocoderRef.current.geocode(
      { location: { lat: Number(point.lat), lng: Number(point.lng) } },
      (results, status) => {
        if (status !== "OK" || !results?.[0]) return;
        liveInfoWindowRef.current?.setContent(
          buildLiveLocationInfoHtml(point, results[0].formatted_address)
        );
      }
    );
  }, [buildLiveLocationInfoHtml, getResolvedCurrentLocationPoint]);

  const attachLiveMarkerClick = useCallback(() => {
    if (!liveMkRef.current) return;
    liveMarkerClickRef.current?.remove?.();
    liveMarkerClickRef.current = liveMkRef.current.addListener("click", openLiveLocationInfo);
  }, [openLiveLocationInfo]);

  const animateLiveMarkerTo = useCallback((target) => {
    if (!liveMkRef.current || !window.google?.maps) return;

    if (liveMarkerAnimationRef.current) {
      cancelAnimationFrame(liveMarkerAnimationRef.current);
      liveMarkerAnimationRef.current = null;
    }

    const currentPosition = liveMkRef.current.getPosition?.();
    const start = currentPosition
      ? { lat: currentPosition.lat(), lng: currentPosition.lng() }
      : target;
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / LIVE_MARKER_ANIMATION_MS);
      const eased = 1 - Math.pow(1 - progress, 3);
      liveMkRef.current?.setPosition({
        lat: start.lat + (target.lat - start.lat) * eased,
        lng: start.lng + (target.lng - start.lng) * eased,
      });

      if (progress < 1) {
        liveMarkerAnimationRef.current = requestAnimationFrame(tick);
      } else {
        liveMarkerAnimationRef.current = null;
      }
    };

    liveMarkerAnimationRef.current = requestAnimationFrame(tick);
  }, []);

  const syncLiveMarker = useCallback((point) => {
    if (!mapLoaded) return;

    const current = getLocationPoint(point);
    if (!current) return;
    const accuracy = Number(current.accuracy ?? 0);
    if (accuracy > LIVE_MARKER_MAX_ACCURACY_METRES) return;

    if (!gMapRef.current || !window.google?.maps) return;

    const G = window.google.maps;
    const rawPos = { lat: current.lat, lng: current.lng };
    const previous = liveMarkerSmoothedRef.current;
    const movedMetres = previous
      ? distMetres(previous.lat, previous.lng, rawPos.lat, rawPos.lng)
      : Infinity;

    if (previous && movedMetres < LIVE_MARKER_MIN_MOVE_METRES) return;

    const accuracyWeight = accuracy > 80 ? 0.18 : accuracy > 40 ? 0.26 : 0.38;
    const blend = movedMetres >= LIVE_MARKER_FORCE_MOVE_METRES ? 0.65 : accuracyWeight;
    const pos = previous
      ? {
          lat: previous.lat + (rawPos.lat - previous.lat) * blend,
          lng: previous.lng + (rawPos.lng - previous.lng) * blend,
          accuracy,
          recordedAt: current.recordedAt || current.time || current.timestamp || new Date().toISOString(),
        }
      : {
          ...rawPos,
          accuracy,
          recordedAt: current.recordedAt || current.time || current.timestamp || new Date().toISOString(),
        };

    liveMarkerSmoothedRef.current = pos;

    if (liveMkRef.current) {
      animateLiveMarkerTo(pos);
    } else {
      const liveIconSvg = `<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="16" fill="rgba(37,99,235,0.25)"/><circle cx="20" cy="20" r="11" fill="rgba(37,99,235,0.45)"/><circle cx="20" cy="20" r="7" fill="#2563eb" stroke="#ffffff" stroke-width="2.5"/><circle cx="20" cy="20" r="2.5" fill="#ffffff"/></svg>`;

      liveMkRef.current = new G.Marker({
        position: pos,
        map: gMapRef.current,
        title: "Current Position",
        zIndex: 2000,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(liveIconSvg)}`,
          size: new G.Size(40, 40),
          scaledSize: new G.Size(40, 40),
          anchor: new G.Point(20, 20),
        },
      });
    }
    attachLiveMarkerClick();
  }, [animateLiveMarkerTo, attachLiveMarkerClick, mapLoaded]);

  const focusCurrentLocation = useCallback(() => {
    const position = getResolvedCurrentLocationPoint(true);

    if (!position) {
      toast.error("Current user location is not available yet.", { title: "Live Location" });
      return;
    }

    userAdjustedViewportRef.current = false;
    if (!gMapRef.current) return;

    withProgrammaticViewport(() => {
      gMapRef.current.panTo(position);
      gMapRef.current.setZoom(18);
    });
  }, [getResolvedCurrentLocationPoint, withProgrammaticViewport]);

  useEffect(() => {
    visitsRef.current = visits;
  }, [visits]);

  useEffect(() => {
    punchInLocationRef.current = punchInLocation;
  }, [punchInLocation]);
  useEffect(() => {
    punchOutLocationRef.current = punchOutLocation;
  }, [punchOutLocation]);

  useEffect(() => {
    currentLocationPointRef.current = currentLocationPoint;
    const resolved = getResolvedCurrentLocationPoint(true);
    if (isLive) syncLiveMarker(resolved || currentLocationPoint);
  }, [currentLocationPoint, isLive, syncLiveMarker, getResolvedCurrentLocationPoint]);

  useEffect(() => {
    routeModeRef.current = routeMode;
  }, [routeMode]);

  // â”€â”€ Init Google Maps â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const initGoogleMap = useCallback(() => {
    loadGoogleMaps()
      .then(() => {
        const container = mapDivRef.current;
        if (!container || gMapRef.current || !window.google?.maps) return;

        const G = window.google.maps;
        const map = new G.Map(container, {
          center: { lat: 20.2961, lng: 85.8245 },
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          gestureHandling: "greedy",
        });

        const markUserViewportChange = () => {
          if (!suppressViewportEventsRef.current) {
            userAdjustedViewportRef.current = true;
          }
        };
        map.addListener("dragstart", markUserViewportChange);
        map.addListener("zoom_changed", markUserViewportChange);

        gMapRef.current = map;
        setMapLoaded(true);
      })
      .catch((error) => {
        setMapError(error.message || "Failed to load Google Maps.");
      });
    return;

  }, []);

  const initMap = useCallback(() => {
    setMapError(null);
    initGoogleMap();
  }, [initGoogleMap]);

  useEffect(() => {
    initMap();
  }, [initMap]);

  // â”€â”€ Place punch-in marker â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    syncStartMarker();
  }, [mapLoaded, punchInLocation, syncStartMarker]);

 // â”€â”€ Draw visit markers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
useEffect(() => {
  if (!mapLoaded) return;


  if (!window.google?.maps || !gMapRef.current) return;
  const G   = window.google.maps;
  const map = gMapRef.current;

  // Clear old visit markers
  visitMkRef.current.forEach((m) => m.setMap(null));
  visitMkRef.current = [];

  visits.forEach((v, i) => {
    if ((v.locationName || "").trim().toLowerCase() === "start location") return;

    const lat = Number(
      v.coordinates?.lat ??
      v.location?.lat ??
      v.location?.latitude ??
      v.lat ??
      v.latitude
    );
    const lng = Number(
      v.coordinates?.lng ??
      v.coordinates?.longitude ??
      v.location?.lng ??
      v.location?.longitude ??
      v.lng ??
      v.longitude
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const isInProgress = v.status === "InProgress";
    const fillColor    = isInProgress ? "#22c55e" : "#4569ea";
    const label        = v.locationName || `Stop ${i + 1}`;

    // Custom SVG store pin
    const svgPin = `
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
          </filter>
        </defs>
        <!-- Pin body -->
        <path d="M18 0C10.268 0 4 6.268 4 14c0 9.941 14 28 14 28S32 23.941 32 14C32 6.268 25.732 0 18 0z"
              fill="${fillColor}" filter="url(#shadow)"/>
        <!-- White circle inside -->
        <circle cx="18" cy="14" r="8" fill="white"/>
        <!-- Store icon (simplified) -->
        <rect x="12" y="11" width="12" height="8" rx="1" fill="${fillColor}"/>
        <rect x="14" y="14" width="3" height="5" fill="white"/>
        <rect x="19" y="14" width="3" height="5" fill="white"/>
        <path d="M11 11 L18 8 L25 11" stroke="${fillColor}" stroke-width="1.5" fill="none"/>
      </svg>`;

    const marker = new G.Marker({
      position: { lat, lng },
      map,
      title:  label,
      zIndex: 30,
      icon: {
        url:    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgPin)}`,
        size:         new G.Size(36, 44),
        anchor:       new G.Point(18, 44),
        scaledSize:   new G.Size(36, 44),
      },
      animation: G.Animation.DROP,
    });

    // Info window on click
    const infoWindow = new G.InfoWindow();

    marker.addListener("click", () => {
      openVisitInfoWindow(infoWindow, map, marker, v, label, lat, lng, fillColor, isInProgress);
    });

    visitMkRef.current.push(marker);
  });
}, [mapLoaded, visits]);

  const syncEndMarker = useCallback((points = allPtsRef.current) => {
    if (!mapLoaded || !gMapRef.current || !window.google?.maps) return;

    const G = window.google.maps;
    const explicitEnd =
      punchOutLocationRef.current?.lat && punchOutLocationRef.current?.lng
        ? { lat: punchOutLocationRef.current.lat, lng: punchOutLocationRef.current.lng }
        : null;
    const trailEnd = points?.length ? points[points.length - 1] : null;
    const markerPos = explicitEnd || (!isLive ? trailEnd : null);

    if (!markerPos) {
      if (endMkRef.current) {
        endMkRef.current.setMap(null);
        endMkRef.current = null;
      }
      return;
    }

    if (endMkRef.current) {
      endMkRef.current.setPosition({ lat: markerPos.lat, lng: markerPos.lng });
    } else {
      endMkRef.current = new G.Marker({
        position: { lat: markerPos.lat, lng: markerPos.lng },
        map: gMapRef.current,
        title: explicitEnd ? "Punch-out Location" : "Tracking Stopped",
        zIndex: 18,
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: "#111827",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 3,
        },
      });
    }
  }, [mapLoaded, isLive]);
  

  // â”€â”€ Drawing Manager â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!mapLoaded || isOwner || !window.google?.maps) return;

    const G   = window.google.maps;
    const map = gMapRef.current;
    if (!map || !drawMode) return;

    clearDraftFence();
    map.setOptions({ draggableCursor: "crosshair" });

    const listener = map.addListener("click", async (event) => {
      if (!event.latLng) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };

      if (drawMode === DRAW_MODE_CIRCLE) {
        const radiusInput = window.prompt("Radius in metres:", "100");
        const radius = Number(radiusInput);
        if (!Number.isFinite(radius) || radius <= 0) {
          toast.error("Enter a valid radius in metres.");
          return;
        }

        const name = window.prompt("Name this geofence:");
        if (!name) return;

        const circle = new G.Circle({
          center: point,
          radius,
          map,
          strokeColor: "#FF6B35",
          strokeWeight: 2,
          fillColor: "#FF6B35",
          fillOpacity: 0.12,
          editable: true,
          draggable: true,
        });

        setDrawMode(null);
        await saveGeofence({
          name,
          type: "circle",
          center: { lat: circle.getCenter().lat(), lng: circle.getCenter().lng() },
          radius: circle.getRadius(),
        }, circle);
        return;
      }

      const draft = draftFenceRef.current;
      const path = [...draft.path, point];
      draft.markers.push(new G.Marker({
        position: point,
        map,
        zIndex: 2000,
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: 5,
          fillColor: "#FF6B35",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
      }));

      if (draft.overlay) {
        draft.overlay.setPath(path);
      } else {
        draft.overlay = new G.Polygon({
          paths: path,
          map,
          strokeColor: "#FF6B35",
          strokeWeight: 2,
          fillColor: "#FF6B35",
          fillOpacity: 0.12,
          editable: true,
        });
      }

      draft.path = path;
      draftFenceRef.current = draft;
    });

    return () => {
      listener.remove();
      map.setOptions({ draggableCursor: null });
    };
  }, [mapLoaded, isOwner, drawMode, clearDraftFence, saveGeofence]);

  // â”€â”€ drawTrail â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  function buildSnapBatchKey(points) {
    return points
      .map((p) => `${Number(p.lat).toFixed(6)},${Number(p.lng).toFixed(6)}`)
      .join("|");
  }

  async function fetchSnappedBatch(batch) {
    if (!Array.isArray(batch) || batch.length < 2) {
      return batch.map((point) => ({ lat: point.lat, lng: point.lng }));
    }

    const path = batch
      .map((point) => `${Number(point.lat).toFixed(6)},${Number(point.lng).toFixed(6)}`)
      .join("|");

    const url =
      `https://roads.googleapis.com/v1/snapToRoads?interpolate=true&path=${encodeURIComponent(path)}&key=${GKEY}`;

    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Snap to Roads failed with status ${res.status}`);
    }

    const data = await res.json();
    const snapped = Array.isArray(data?.snappedPoints)
      ? data.snappedPoints
          .map((point) => ({
            lat: Number(point?.location?.latitude),
            lng: Number(point?.location?.longitude),
          }))
          .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
      : [];

    return snapped.length
      ? snapped
      : batch.map((point) => ({ lat: point.lat, lng: point.lng }));
  }

  function fetchRoadConnectorPath(origin, destination) {
    return new Promise((resolve) => {
      if (!window.google?.maps || !origin || !destination) {
        resolve([]);
        return;
      }

      const G = window.google.maps;
      const service = new G.DirectionsService();
      service.route(
        {
          origin: { lat: Number(origin.lat), lng: Number(origin.lng) },
          destination: { lat: Number(destination.lat), lng: Number(destination.lng) },
          travelMode: G.TravelMode.DRIVING,
          provideRouteAlternatives: false,
        },
        (result, status) => {
          if (status !== "OK") {
            resolve([]);
            return;
          }

          const path = result?.routes?.[0]?.overview_path;
          resolve(
            Array.isArray(path)
              ? path.map((point) => ({ lat: point.lat(), lng: point.lng() }))
              : []
          );
        }
      );
    });
  }

  function appendPath(target, path) {
    for (const point of path) {
      if (!point || !Number.isFinite(Number(point.lat)) || !Number.isFinite(Number(point.lng))) continue;
      const normalized = { lat: Number(point.lat), lng: Number(point.lng) };
      const last = target[target.length - 1];
      if (
        last &&
        Math.abs(last.lat - normalized.lat) < 0.000001 &&
        Math.abs(last.lng - normalized.lng) < 0.000001
      ) {
        continue;
      }
      target.push(normalized);
    }
  }

  async function getRoadPath(points) {
    if (!Array.isArray(points) || points.length < 2) {
      return points.map((point) => ({ lat: point.lat, lng: point.lng }));
    }

    const cacheKey = buildSnapBatchKey(points);
    if (snappedTrailCacheRef.current.key === cacheKey) {
      return snappedTrailCacheRef.current.path;
    }

    const snappedPath = [];
    let currentBatch = [points[0]];
    let lastBatchEnd = points[0];

    const flushCurrentBatch = async () => {
      if (currentBatch.length < 2) return;
      const snappedBatch = await fetchSnappedBatch(currentBatch);
      appendPath(snappedPath, snappedBatch);
      lastBatchEnd = currentBatch[currentBatch.length - 1];
    };

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const gapMetres = distMetres(prev.lat, prev.lng, curr.lat, curr.lng);

      if (
        gapMetres > SNAP_TO_ROADS_MAX_GAP_METRES ||
        currentBatch.length >= SNAP_TO_ROADS_MAX_POINTS
      ) {
        await flushCurrentBatch();

        if (gapMetres > SNAP_TO_ROADS_MAX_GAP_METRES) {
          if (gapMetres <= 30000) {
            const connectorPath = await fetchRoadConnectorPath(prev, curr);
            if (connectorPath.length >= 2) appendPath(snappedPath, connectorPath);
          }
          currentBatch = [curr];
        } else {
          currentBatch = [lastBatchEnd, prev, curr].filter(Boolean);
        }
        continue;
      }

      currentBatch.push(curr);
    }

    await flushCurrentBatch();

    const finalPath = snappedPath.length
      ? snappedPath
      : points.map((point) => ({ lat: point.lat, lng: point.lng }));

    snappedTrailCacheRef.current = { key: cacheKey, path: finalPath };
    return finalPath;
  }

  function clearGpsDots() {
    dotsRef.current.forEach((d) => d.setMap(null));
    dotsRef.current = [];
    setGpsDotCount(0);
  }

function getGpsDotColor(accuracyValue = 0) {
  const value = Number(accuracyValue) || 0;
  if (!value || value <= 20) return "#16a34a";
  if (value <= 50) return "#d97706";
  return "#dc2626";
}

function buildRoadSnapPoints(rawPoints = [], fallbackPoints = []) {
  const source = Array.isArray(rawPoints) && rawPoints.length >= 2 ? rawPoints : fallbackPoints;
  const normalized = orderPointsByTime(source)
    .map((point) => ({
      lat: Number(point?.lat),
      lng: Number(point?.lng),
      accuracy: Number(point?.accuracy ?? 0),
      recordedAt: point?.recordedAt || point?.time || null,
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .filter((point) => !point.accuracy || point.accuracy <= 100);

  if (normalized.length <= 2) return normalized;

  const simplified = [normalized[0]];
  for (let i = 1; i < normalized.length - 1; i += 1) {
    const previous = simplified[simplified.length - 1];
    const current = normalized[i];
    const next = normalized[i + 1];

    const distanceFromPrevious = distMetres(previous.lat, previous.lng, current.lat, current.lng);
    const distanceToNext = distMetres(current.lat, current.lng, next.lat, next.lng);
    const accuracyBuffer = Math.max(
      10,
      Math.min(30, Math.max(previous.accuracy || 0, current.accuracy || 0, next.accuracy || 0) * 0.5)
    );

    if (distanceFromPrevious < accuracyBuffer && distanceToNext < accuracyBuffer) {
      continue;
    }

    simplified.push(current);
  }

  simplified.push(normalized[normalized.length - 1]);
  return simplified;
}

function buildDisplayRoutePoints(rawPoints = [], fallbackPoints = []) {
  const source = Array.isArray(rawPoints) && rawPoints.length >= 2 ? rawPoints : fallbackPoints;
  const ordered = orderPointsByTime(source)
    .map((point) => ({
      lat: Number(point?.lat),
      lng: Number(point?.lng),
      accuracy: Number(point?.accuracy ?? 0),
      recordedAt: point?.recordedAt || point?.time || null,
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .filter((point) => !point.accuracy || point.accuracy <= 180);

  const displayPoints = [];
  for (const point of ordered) {
    const last = displayPoints[displayPoints.length - 1];
    if (!last) {
      displayPoints.push(point);
      continue;
    }

    const distM = distMetres(last.lat, last.lng, point.lat, point.lng);
    if (distM < 2) continue;

    const lastTime = getPointTimestamp(last);
    const currentTime = getPointTimestamp(point);
    const elapsedHours =
      lastTime !== null && currentTime !== null && currentTime > lastTime
        ? (currentTime - lastTime) / 3600000
        : null;
    const speedKmh = elapsedHours ? (distM / 1000) / elapsedHours : 0;

    // This is only for map display. Keep legitimate sparse tracking gaps,
    // but reject obvious GPS teleports that would draw the route across the map.
    if (distM > 30000) continue;
    if (elapsedHours && speedKmh > 180 && distM > 1000) continue;

    displayPoints.push(point);
  }

  return displayPoints.length >= 2 ? displayPoints : buildRoadSnapPoints(rawPoints, fallbackPoints);
}

  function getLineStyle(mode) {
    if (mode === ROUTE_MODE_RAW) {
      return {
        casingColor: "#ffffff",
        casingWeight: 8,
        mainColor: "#ef4444",
        mainWeight: 4,
        mainOpacity: 0.9,
      };
    }

    return {
      casingColor: "#ffffff",
      casingWeight: 8,
      mainColor: "#ef4444",
      mainWeight: 5,
      mainOpacity: 0.95,
    };
  }

  function clearHeatmapRoute() {
    heatmapRouteRef.current.casings.forEach((line) => line.setMap(null));
    heatmapRouteRef.current.lines.forEach((line) => line.setMap(null));
    heatmapRouteRef.current.labels.forEach((label) => label.setMap(null));
    heatmapRouteRef.current = { casings: [], lines: [], labels: [] };
  }

  function getRouteSegmentKey(a, b) {
    const first = `${Number(a.lat).toFixed(4)},${Number(a.lng).toFixed(4)}`;
    const second = `${Number(b.lat).toFixed(4)},${Number(b.lng).toFixed(4)}`;
    return first < second ? `${first}|${second}` : `${second}|${first}`;
  }

  function getHeatmapColor(count, maxCount) {
    const ratio = maxCount <= 1 ? 0 : Math.min(1, Math.max(0, (count - 1) / (maxCount - 1)));
    const stops = [
      { at: 0, color: [248, 113, 113] },
      { at: 0.35, color: [239, 68, 68] },
      { at: 0.7, color: [185, 28, 28] },
      { at: 1, color: [127, 29, 29] },
    ];
    const upperIndex = stops.findIndex((stop) => ratio <= stop.at);
    const upper = stops[Math.max(1, upperIndex)];
    const lower = stops[stops.indexOf(upper) - 1] || stops[0];
    const localRatio = upper.at === lower.at ? 0 : (ratio - lower.at) / (upper.at - lower.at);
    const color = lower.color.map((channel, index) =>
      Math.round(channel + (upper.color[index] - channel) * localRatio)
    );
    return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
  }

  function getHeatmapZoomStyle() {
    const zoom = gMapRef.current?.getZoom?.() || 14;
    const zoomFactor = Math.min(1.45, Math.max(0.7, zoom / 16));
    return {
      zoom,
      lineFactor: zoomFactor,
      labelScale: Math.round(10 + zoomFactor * 3),
      labelFont: Math.round(9 + zoomFactor * 2),
      labelMinMetres: zoom >= 17 ? 70 : zoom >= 15 ? 120 : 220,
      maxLabels: zoom >= 17 ? 18 : zoom >= 15 ? 12 : 7,
    };
  }

  function createHeatmapBadgeSvg(count, size, fontSize) {
    const label = `x${count}`;
    const width = Math.max(size * 2.35, 28 + String(count).length * 7);
    const height = size * 1.6;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <filter id="shadow" x="-30%" y="-40%" width="160%" height="190%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(15,23,42,0.22)"/>
        </filter>
        <rect x="1.5" y="1.5" width="${width - 3}" height="${height - 3}" rx="${height / 2 - 1.5}" fill="#fff" stroke="#b91c1c" stroke-width="2" filter="url(#shadow)"/>
        <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="Inter, Arial, sans-serif" font-size="${fontSize}" font-weight="800" fill="#7f1d1d">${label}</text>
      </svg>`;
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      width,
      height,
    };
  }

  function buildHeatmapSegments(path = []) {
    const segments = new Map();

    for (let i = 1; i < path.length; i++) {
      const prev = path[i - 1];
      const curr = path[i];
      if (!prev || !curr) continue;
      const distance = distMetres(prev.lat, prev.lng, curr.lat, curr.lng);
      if (!Number.isFinite(distance) || distance < 4 || distance > 250) continue;

      const key = getRouteSegmentKey(prev, curr);
      const existing = segments.get(key);
      const start = { lat: Number(prev.lat), lng: Number(prev.lng) };
      const end = { lat: Number(curr.lat), lng: Number(curr.lng) };
      if (existing) {
        existing.count += 1;
        existing.totalDistance += distance;
      } else {
        segments.set(key, {
          key,
          count: 1,
          totalDistance: distance,
          path: [start, end],
          mid: {
            lat: (start.lat + end.lat) / 2,
            lng: (start.lng + end.lng) / 2,
          },
        });
      }
    }

    return Array.from(segments.values());
  }

  function pointsEqual(a, b, precision = 0.00004) {
    return (
      Math.abs(Number(a?.lat) - Number(b?.lat)) <= precision &&
      Math.abs(Number(a?.lng) - Number(b?.lng)) <= precision
    );
  }

  function buildContinuousHeatmapRuns(path = []) {
    const segmentCounts = new Map();
    const orderedSegments = [];

    for (let i = 1; i < path.length; i++) {
      const prev = path[i - 1];
      const curr = path[i];
      if (!prev || !curr) continue;
      const distance = distMetres(prev.lat, prev.lng, curr.lat, curr.lng);
      if (!Number.isFinite(distance) || distance < 4 || distance > 350) continue;
      const segment = {
        start: { lat: Number(prev.lat), lng: Number(prev.lng) },
        end: { lat: Number(curr.lat), lng: Number(curr.lng) },
        key: getRouteSegmentKey(prev, curr),
        distance,
      };
      segmentCounts.set(segment.key, (segmentCounts.get(segment.key) || 0) + 1);
      orderedSegments.push(segment);
    }

    const runs = [];
    for (const segment of orderedSegments) {
      const count = segmentCounts.get(segment.key) || 1;
      const lastRun = runs[runs.length - 1];
      const lastPoint = lastRun?.path?.[lastRun.path.length - 1];
      if (
        lastRun &&
        lastRun.count === count &&
        lastPoint &&
        pointsEqual(lastPoint, segment.start)
      ) {
        lastRun.path.push(segment.end);
        lastRun.totalDistance += segment.distance;
      } else {
        runs.push({
          count,
          path: [segment.start, segment.end],
          totalDistance: segment.distance,
        });
      }
    }

    return runs.map((run) => {
      const middleIndex = Math.floor(run.path.length / 2);
      const a = run.path[Math.max(0, middleIndex - 1)];
      const b = run.path[Math.min(run.path.length - 1, middleIndex)];
      return {
        ...run,
        mid: {
          lat: (Number(a.lat) + Number(b.lat)) / 2,
          lng: (Number(a.lng) + Number(b.lng)) / 2,
        },
      };
    });
  }

  function drawHeatmapRoute(path = []) {
    clearHeatmapRoute();
    if (!gMapRef.current || !window.google?.maps || routeModeRef.current !== ROUTE_MODE_ROAD) return;
    if (!Array.isArray(path) || path.length < 2) return;

    const G = window.google.maps;
    const map = gMapRef.current;

    const casing = new G.Polyline({
      path,
      geodesic: true,
      strokeColor: "#ffffff",
      strokeOpacity: 0.95,
      strokeWeight: 8,
      map,
      zIndex: 1000,
    });

    const line = new G.Polyline({
      path,
      geodesic: true,
      strokeColor: "#ef4444",
      strokeOpacity: 0.95,
      strokeWeight: 5,
      map,
      zIndex: 1001,
    });

    heatmapRouteRef.current.casings.push(casing);
    heatmapRouteRef.current.lines.push(line);
  }

  function drawGpsDots(rawPoints = []) {
    if (!gMapRef.current || !window.google?.maps) return;
    const G = window.google.maps;
    const map = gMapRef.current;

    dotsRef.current.forEach((d) => d.setMap(null));
    dotsRef.current = [];

    const dotPoints = (Array.isArray(rawPoints) ? rawPoints : [])
      .map((point) => ({
        lat: Number(point?.lat),
        lng: Number(point?.lng),
        accuracy: Number(point?.accuracy ?? 0),
        recordedAt: point?.recordedAt || point?.time || null,
      }))
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

    setGpsDotCount(dotPoints.length);
    if (!dotPoints.length || routeMode !== ROUTE_MODE_RAW) return;

    const step = Math.max(1, Math.ceil(dotPoints.length / MAX_VISIBLE_GPS_DOTS));
    dotPoints.forEach((point, index) => {
      if (index % step !== 0) return;
      const marker = new G.Marker({
        position: { lat: point.lat, lng: point.lng },
        map,
        title: [
          `GPS point ${index + 1}`,
          point.accuracy ? `accuracy +/-${Math.round(point.accuracy)} m` : null,
          point.recordedAt ? new Date(point.recordedAt).toLocaleTimeString() : null,
        ].filter(Boolean).join(" | "),
        zIndex: 12,
        icon: {
          path: G.SymbolPath.CIRCLE,
          scale: routeMode === ROUTE_MODE_RAW ? 4 : 3,
          fillColor: getGpsDotColor(point.accuracy),
          fillOpacity: 0.95,
          strokeColor: "#ffffff",
          strokeOpacity: 1,
          strokeWeight: 1.5,
        },
      });
      dotsRef.current.push(marker);
    });
  }

  async function drawTrail(points, options = {}) {
    const { autoFit = false, autoPanLive = false, rawPoints = points } = options;


    if (!gMapRef.current || !window.google?.maps) {
      const hasMoved = hasConfirmedMovement(points);
      const km = calcTrustedKm(points, [], hasMoved);
      updateDistanceStats(km, Array.isArray(points) ? points.length : 0, {
        source: "google-filtered-gps",
        visualRouteKm: 0,
        gpsKm: calcFilteredKm(points),
        routeMode,
      });
      return;
    }
    const G   = window.google.maps;
    const map = gMapRef.current;
    if (!Array.isArray(points) || points.length === 0) {
      polyRef.current.casing?.setMap(null);
      polyRef.current.main?.setMap(null);
      polyRef.current = { casing: null, main: null };
      clearHeatmapRoute();
      clearGpsDots();
      syncEndMarker([]);
      syncStartMarker([]);
      updateDistanceStats(0, 0);
      return;
    }
    const displaySourcePoints = buildDisplayRoutePoints(rawPoints, points);
    const hasMoved = hasConfirmedMovement(points);
    const shouldDrawLine = displaySourcePoints.length >= 2;
    const requestSeq = ++drawRequestSeqRef.current;
    const lineStyle = getLineStyle(routeMode);
    let roadSourcePoints =
      routeMode === ROUTE_MODE_ROAD
        ? buildRoadSnapPoints(displaySourcePoints, points)
        : displaySourcePoints;
    if (roadSourcePoints.length < 2 && displaySourcePoints.length >= 2) {
      roadSourcePoints = displaySourcePoints;
    }

    let path = roadSourcePoints.map((p) => ({ lat: p.lat, lng: p.lng }));
    if (shouldDrawLine && routeMode === ROUTE_MODE_ROAD) {
      try {
        path = await getRoadPath(roadSourcePoints);
      } catch (error) {
        console.warn("[SnapToRoads] Falling back to raw path:", error?.message || error);
      }
    }

    if (requestSeq !== drawRequestSeqRef.current) return;

    if (shouldDrawLine && routeMode === ROUTE_MODE_ROAD) {
      polyRef.current.casing?.setMap(null);
      polyRef.current.main?.setMap(null);
      polyRef.current = { casing: null, main: null };
      heatmapPathRef.current = path;
      drawHeatmapRoute(path);
    } else if (shouldDrawLine) {
      clearHeatmapRoute();
      heatmapPathRef.current = [];
      if (polyRef.current.casing && polyRef.current.main) {
        polyRef.current.casing.setPath(path);
        polyRef.current.main.setPath(path);
      } else {
        polyRef.current = {
          casing: new G.Polyline({
            path,
            geodesic: true,
            strokeColor: lineStyle.casingColor,
            strokeOpacity: 1,
            strokeWeight: lineStyle.casingWeight,
            map,
            zIndex: 1000,
          }),
          main: new G.Polyline({
            path,
            geodesic: true,
            strokeColor: lineStyle.mainColor,
            strokeOpacity: lineStyle.mainOpacity,
            strokeWeight: lineStyle.mainWeight,
            map,
            zIndex: 1001,
          }),
        };
      }
      polyRef.current.casing?.setOptions({
        strokeColor: lineStyle.casingColor,
        strokeWeight: lineStyle.casingWeight,
      });
      polyRef.current.main?.setOptions({
        strokeColor: lineStyle.mainColor,
        strokeOpacity: lineStyle.mainOpacity,
        strokeWeight: lineStyle.mainWeight,
      });
    } else {
      polyRef.current.casing?.setMap(null);
      polyRef.current.main?.setMap(null);
      polyRef.current = { casing: null, main: null };
      clearHeatmapRoute();
      heatmapPathRef.current = [];
    }

    drawGpsDots(rawPoints);

    const last = points[points.length - 1];
    if (last) {
      if (!isLive && liveMkRef.current) {
        liveMarkerClickRef.current?.remove?.();
        liveMarkerClickRef.current = null;
        liveInfoWindowRef.current?.close?.();
        liveMkRef.current.setMap(null);
        liveMkRef.current = null;
      }
      if (isLive && autoPanLive && !userAdjustedViewportRef.current) {
        withProgrammaticViewport(() => {
          map.panTo({ lat: last.lat, lng: last.lng });
        });
      }
    }

    syncEndMarker(points);
    syncStartMarker(points);

    if (shouldDrawLine && !isLive && autoFit && !userAdjustedViewportRef.current) {
      const bounds = new G.LatLngBounds();
      path.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      withProgrammaticViewport(() => {
        map.fitBounds(bounds, { top: 60, bottom: 40, left: 40, right: 40 });
      });
    }

    const filteredKm = calcFilteredKm(points);
    const visualRouteKm = shouldDrawLine && path.length >= 2 ? calcKm(path) : 0;
    const km = calcTrustedKm(points, shouldDrawLine ? path : [], hasMoved);

    updateDistanceStats(km, points.length, {
      source: routeMode === ROUTE_MODE_ROAD ? "map-road-route" : "map-raw-gps",
      visualRouteKm,
      gpsKm: filteredKm,
      routeMode,
    });
    console.log(`[drawTrail] ${points.length} points â€” ${km.toFixed(3)} km`);
  }

  function cleanPoints(raw, options = {}) {
    const { allowSparse = false, preserveDetail = false } = options;
    if (!Array.isArray(raw)) return [];
    const cleaned = [];
    for (const p of orderPointsByTime(raw)) {
      const lat = Number(p.lat);
      const lng = Number(p.lng);
      const accuracy = Number(p.accuracy ?? 0);
      const distanceFromPrevious = Number(p.distanceFromPrevious ?? 0);
      const recordedAt = p.recordedAt || p.time || null;
      const accuracyLimit = allowSparse ? 150 : preserveDetail ? 100 : 80;

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      if (accuracy && accuracy > accuracyLimit) continue;

      if (cleaned.length === 0) {
        cleaned.push({ lat, lng, accuracy, distanceFromPrevious, recordedAt });
        continue;
      }

      const prev = cleaned[cleaned.length - 1];
      const distM = distMetres(prev.lat, prev.lng, lat, lng);
      const requiredMove = allowSparse
        ? Math.max(5, Math.min(getEffectiveMoveThreshold(accuracy, prev.accuracy), 18))
        : preserveDetail
          ? Math.max(3, Math.min(getEffectiveMoveThreshold(accuracy, prev.accuracy), 12))
        : Math.max(
            getEffectiveMoveThreshold(accuracy, prev.accuracy),
            distanceFromPrevious > 0 ? 10 : 14
          );
      const gpsNoiseRadius = allowSparse
        ? Math.max(5, Math.min(getGpsNoiseRadiusMetres(accuracy, prev.accuracy), 18))
        : preserveDetail
          ? 0
        : getGpsNoiseRadiusMetres(accuracy, prev.accuracy);

      if (distanceFromPrevious <= 0 && distM < (allowSparse ? 5 : preserveDetail ? 3 : 8)) continue;
      if (distM < requiredMove && distanceFromPrevious <= 0) continue;
      if (!allowSparse && !preserveDetail && distM < gpsNoiseRadius) continue;
      const prevTime = new Date(prev.recordedAt || prev.time || 0).getTime();
      const currTime = new Date(recordedAt || 0).getTime();
      const elapsedHours =
        Number.isFinite(prevTime) && Number.isFinite(currTime) && currTime > prevTime
          ? (currTime - prevTime) / 3600000
          : null;
      const maxBySpeed = elapsedHours
        ? Math.max(150, elapsedHours * MAX_COUNTED_SPEED_KMH * 1000)
        : MAX_COUNTED_SEGMENT_METRES;
      if (!allowSparse && distM > Math.min(MAX_COUNTED_SEGMENT_METRES, maxBySpeed)) continue;

      cleaned.push({ lat, lng, accuracy, distanceFromPrevious, recordedAt });
    }

    return cleaned;
  }

  const fetchRoadTravelDistance = useCallback(async () => {
    if (!userId) return;
    setRoadTravel((prev) => ({ ...prev, loading: true }));
    const dateParam = getLocalDateString(selectedDate);
    const requestDistance = async (endpoint) => {
      const res = await fetch(
        `${API}/api/v1${endpoint}?salesmanId=${userId}&date=${dateParam}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (!res.ok) {
        throw new Error(`Distance request failed with status ${res.status}`);
      }

      const data = await res.json();
      return data?.data || data?.result || data || {};
    };

    try {
      const result = await requestDistance("/location/mapbox-distance");
      const roadKm = Number(result.totalKm);
      setRoadTravel({
        totalKm: Number.isFinite(roadKm) ? roadKm : null,
        segmentCount: Number(result.segmentCount || result.segments?.length || 0),
        source: result.source || "mapbox",
        status: result.status || null,
        quality: result.quality || null,
        confidence: result.confidence ?? null,
        mapboxConfigured: result.mapboxConfigured ?? null,
        loading: false,
      });
    } catch {
      try {
        const fallback = await requestDistance("/location/travel-distance");
        const fallbackKm = Number(fallback.totalKm);
        setRoadTravel({
          totalKm: Number.isFinite(fallbackKm) ? fallbackKm : null,
          segmentCount: Number(fallback.segmentCount || fallback.segments?.length || 0),
          source: fallback.source || "google-road",
          status: "mapbox_fallback",
          quality: null,
          confidence: null,
          mapboxConfigured: false,
          loading: false,
        });
        return;
      } catch {
        // Keep the UI usable even if both distance services fail.
      }

      setRoadTravel({
        totalKm: null,
        segmentCount: 0,
        source: null,
        status: null,
        quality: null,
        confidence: null,
        mapboxConfigured: null,
        loading: false,
      });
    }
  }, [userId, selectedDate]);

  const fetchTotalKm = useCallback(async () => {
    if (!userId) return;
    try {
       const dateParam = getLocalDateString(selectedDate);
      const res = await fetch(
        `${API}/api/v1/location/distance?salesmanId=${userId}&date=${dateParam}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );

      if (!res.ok) {
        if (allPtsRef.current.length >= 2) {
          updateDistanceStats(calcTrustedKm(allPtsRef.current), allPtsRef.current.length);
        }
        return;
      }
      const data = await res.json();
      const dbKm = data?.data?.totalKm ?? data?.result?.totalKm ?? data?.totalKm ?? 0;
      const dbPoints = data?.data?.totalPoints ?? data?.result?.totalPoints ?? data?.totalPoints ?? 0;
      if (Number(dbKm) > 0 || Number(dbPoints) > 0) {
        updateDistanceStats(Math.round(Number(dbKm) * 1000) / 1000, Number(dbPoints) || allPtsRef.current.length);
      } else if (allPtsRef.current.length >= 2) {
        updateDistanceStats(calcTrustedKm(allPtsRef.current), allPtsRef.current.length);
      }
    } catch { /* non-fatal */ }
  }, [userId, selectedDate, updateDistanceStats]);

  const loadTrailFromDB = useCallback(async (options = {}) => {
    const { autoFit = false } = options;
    if (!userId || !mapLoaded) return;
    if (preferVisitTrail) return;
     try {
      // âœ… Use selectedDate if provided, otherwise fall back to today
      let since, until;
      if (selectedDate && selectedDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [yyyy, mm, dd] = selectedDate.split("-").map(Number);
        since = new Date(yyyy, mm - 1, dd, 0, 0, 0, 0);
        until = new Date(yyyy, mm - 1, dd, 23, 59, 59, 999);
      } else {
        const now = new Date();
        since = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        until = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }

      const url = `${API}/api/v1/location/today?salesmanId=${userId}&startTime=${since.toISOString()}&endTime=${until.toISOString()}`;

      console.log(`[AdminMap] ðŸ“¡ Fetching trail for userId:${userId}`);

      const res     = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data    = await res.json();
      console.log("[AdminMap] Full response:", JSON.stringify(data).slice(0, 300));

      const raw = extractLocationPoints(data);
      const rawAfterPunchIn = filterPointsFromPunchInTime(
        raw,
        punchInLocationRef.current?.time || null
      );
      rawPtsRef.current = rawAfterPunchIn;
      const preserveDetail = routeMode === ROUTE_MODE_RAW;
      const cleaned = cleanPoints(rawAfterPunchIn, { preserveDetail });
      const sparseCleaned = cleaned.length >= 2
        ? cleaned
        : cleanPoints(rawAfterPunchIn, { allowSparse: true, preserveDetail });
      const visitFallbackTrail = extractVisitTrailPoints(
        visitsRef.current,
        punchInLocationRef.current
      );
      const rawDisplayTrail = buildDisplayRoutePoints(rawAfterPunchIn, visitFallbackTrail);

      console.log(
        `[AdminMap] DB points: raw=${raw.length} afterPunchIn=${rawAfterPunchIn.length} cleaned=${cleaned.length} sparse=${sparseCleaned.length} visitFallback=${visitFallbackTrail.length}`
      );

      const shouldUseVisitFallback =
        sparseCleaned.length < 2 && visitFallbackTrail.length >= 2;

      const baseTrail = shouldUseVisitFallback
        ? visitFallbackTrail
        : sparseCleaned.length > 0
          ? sparseCleaned
          : rawDisplayTrail.length > 0
            ? rawDisplayTrail
            : visitFallbackTrail;

      if (baseTrail.length > 0) {
        const merged = ensureTrailStartsAtPunchIn(
          [...baseTrail],
          punchInLocationRef.current
        );
        if (isLive && isTodaySelected) {
          for (const lp of allPtsRef.current) {
            appendSequentialPoint(merged, lp);
          }
        }
        let finalTrail = cleanPoints(merged, {
          allowSparse: shouldUseVisitFallback,
          preserveDetail: preserveDetail && !shouldUseVisitFallback,
        });
        if (finalTrail.length < 2 && rawDisplayTrail.length >= 2) {
          finalTrail = ensureTrailStartsAtPunchIn(rawDisplayTrail, punchInLocationRef.current);
        }
        allPtsRef.current = finalTrail;
        drawTrail(finalTrail, { autoFit, autoPanLive: false, rawPoints: rawAfterPunchIn });
        console.log(`[AdminMap] âœ… Trail: ${finalTrail.length} points â€” ${calcKm(finalTrail).toFixed(3)} km`);
      } else {
        if (allPtsRef.current.length > 0) {
          drawTrail(allPtsRef.current, { autoFit, autoPanLive: false, rawPoints: rawAfterPunchIn });
        }
        console.warn(`[AdminMap] âš ï¸ No DB points â€” raw:${raw.length} afterPunchIn:${rawAfterPunchIn.length}`);
      }

      await fetchTotalKm();
      await fetchRoadTravelDistance();
    } catch (e) {
      console.error("[AdminMap] âŒ Trail fetch failed:", e.message);
    }
  }, [userId, mapLoaded, fetchTotalKm, fetchRoadTravelDistance, selectedDate, isLive, isTodaySelected, preferVisitTrail, routeMode]);

  useEffect(() => {
    // âœ… Clear old trail and re-fetch whenever selectedDate changes
    polyRef.current.casing?.setMap(null);
    polyRef.current.main?.setMap(null);
    polyRef.current = { casing: null, main: null };
    dotsRef.current.forEach(d => d.setMap(null));
    dotsRef.current = [];
    liveMarkerSmoothedRef.current = null;
    if (liveMkRef.current) {
      liveMarkerClickRef.current?.remove?.();
      liveMarkerClickRef.current = null;
      liveInfoWindowRef.current?.close?.();
      if (liveMarkerAnimationRef.current) {
        cancelAnimationFrame(liveMarkerAnimationRef.current);
        liveMarkerAnimationRef.current = null;
      }
      liveMkRef.current.setMap(null);
      liveMkRef.current = null;
    }
    if (endMkRef.current) { endMkRef.current.setMap(null); endMkRef.current = null; }
    allPtsRef.current = [];
    rawPtsRef.current = [];
    snappedTrailCacheRef.current = { key: "", path: [] };
    drawRequestSeqRef.current += 1;
    userAdjustedViewportRef.current = false;
    setGpsDotCount(0);
    updateDistanceStats(0, 0);
    if (!preferVisitTrail) {
      loadTrailFromDB({ autoFit: true });
    }
  }, [loadTrailFromDB, updateDistanceStats, preferVisitTrail]);

  useEffect(() => {
    if (!mapLoaded || isLive) return;

    const visitFallbackTrail = extractVisitTrailPoints(visits, punchInLocation);
    if (allPtsRef.current.length >= 2 || visitFallbackTrail.length < 2) {
      syncEndMarker(allPtsRef.current);
      return;
    }

      const finalTrail = cleanPoints(
        ensureTrailStartsAtPunchIn(visitFallbackTrail, punchInLocation),
        { allowSparse: true }
      );
    if (finalTrail.length >= 2) {
      allPtsRef.current = finalTrail;
      drawTrail(finalTrail, { autoFit: true, autoPanLive: false });
      updateDistanceStats(calcTrustedKm(finalTrail), finalTrail.length);
    } else {
      syncEndMarker(finalTrail);
    }
  }, [mapLoaded, isLive, visits, punchInLocation, syncEndMarker, updateDistanceStats]);

  useEffect(() => {
    if (!mapLoaded || !preferVisitTrail) return;

    const visitTrail = extractVisitTrailPoints(visits, punchInLocation);
    const finalTrail = cleanPoints(
      ensureTrailStartsAtPunchIn(visitTrail, punchInLocation),
      { allowSparse: true }
    );

    if (finalTrail.length >= 2) {
      allPtsRef.current = finalTrail;
      drawTrail(finalTrail, { autoFit: true, autoPanLive: false });
      updateDistanceStats(calcTrustedKm(finalTrail), finalTrail.length);
      return;
    }

    if (finalTrail.length === 1) {
      allPtsRef.current = finalTrail;
      syncStartMarker(finalTrail);
      syncEndMarker(finalTrail);
      updateDistanceStats(0, finalTrail.length);
      return;
    }

    allPtsRef.current = [];
    syncStartMarker([]);
    syncEndMarker([]);
    updateDistanceStats(0, 0);
  }, [mapLoaded, preferVisitTrail, visits, punchInLocation, syncEndMarker, syncStartMarker, updateDistanceStats]);

  useEffect(() => {
    syncEndMarker(allPtsRef.current);
  }, [punchOutLocation, syncEndMarker]);

  useEffect(() => {
    if (preferVisitTrail) return;
    if (!userId || !mapLoaded || !isLive || !isTodaySelected) return;
    const id = setInterval(() => loadTrailFromDB({ autoFit: false }), 10_000);
    return () => clearInterval(id);
  }, [userId, mapLoaded, loadTrailFromDB, isLive, isTodaySelected, preferVisitTrail]);

  useEffect(() => {
    if (!mapLoaded || allPtsRef.current.length === 0) return;
    drawTrail(allPtsRef.current, {
      autoFit: false,
      autoPanLive: false,
      rawPoints: rawPtsRef.current.length ? rawPtsRef.current : allPtsRef.current,
    });
  }, [mapLoaded, routeMode]);

  // â”€â”€ Start / stop tracking on punch â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!mapLoaded) return;

    const wasIn  = prevPunchedIn.current;
    const wasOut = prevPunchedOut.current;
    prevPunchedIn.current  = isPunchedIn;
    prevPunchedOut.current = hasPunchedOut;

    if (isPunchedIn && !hasPunchedOut && !wasIn && isOwner) {
      // âœ… FIX: pass socketRef (stable ref) instead of socket (raw, potentially stale)
      startTracking(
        (allPts) => {
          if (!allPts.length) return;
          const newPts = allPts.map((p) => ({
            lat: p.lat,
            lng: p.lng,
            accuracy: p.accuracy,
            recordedAt: p.recordedAt || p.timestamp || p.time || null,
          }));
          rawPtsRef.current = newPts;
          const merged = [...allPtsRef.current];
          for (const np of newPts) {
            appendSequentialPoint(merged, np);
            }
            const finalTrail = cleanPoints(merged, { preserveDetail: routeMode === ROUTE_MODE_RAW });
            allPtsRef.current = finalTrail;
            drawTrail(finalTrail, { autoFit: false, autoPanLive: false, rawPoints: rawPtsRef.current });
            const last = allPts[allPts.length - 1];
            if (last) setAccuracy(last.accuracy);
            if (typeof onPointsChange === "function") onPointsChange(allPts);
        },
        socketRef,  // âœ… FIX: stable ref, not raw socket
        userId,
      );
    }

    if (hasPunchedOut && !wasOut) {
      if (isCurrentlyTracking()) stopTracking();
      setTimeout(() => loadTrailFromDB({ autoFit: false }), 2000);
    }
  }, [isPunchedIn, hasPunchedOut, socketRef, mapLoaded, loadTrailFromDB, userId, routeMode]);

  // â”€â”€ Live dot every 30 s â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!isOwner || !isPunchedIn || hasPunchedOut || !mapLoaded || !gMapRef.current) return;
    if (!window.google?.maps) return;

    const updateDot = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          const acc = pos.coords.accuracy ?? 0;

          if (acc > LIVE_MARKER_MAX_ACCURACY_METRES) return;

          const livePoint = { lat, lng, accuracy: acc, recordedAt: new Date().toISOString() };
          syncLiveMarker(livePoint);
          const rawMerged = [...rawPtsRef.current];
          appendSequentialPoint(rawMerged, livePoint, 1);
          rawPtsRef.current = rawMerged;

          const lastTrailPt = allPtsRef.current[allPtsRef.current.length - 1];
          if (lastTrailPt) {
            const moved = distMetres(lastTrailPt.lat, lastTrailPt.lng, lat, lng);
            const minMoveRequired = routeMode === ROUTE_MODE_RAW
              ? 3
              : getEffectiveMoveThreshold(acc, lastTrailPt.accuracy);
            if (moved >= minMoveRequired) {
              const merged = [...allPtsRef.current, livePoint];
              const finalTrail = cleanPoints(merged, { preserveDetail: routeMode === ROUTE_MODE_RAW });
              allPtsRef.current = finalTrail;
              drawTrail(finalTrail, { autoFit: false, autoPanLive: false, rawPoints: rawPtsRef.current });
              console.log(`[LiveDot] âœ… ${moved.toFixed(0)} m real movement â€” trail updated`);
            } else {
              console.log(`[LiveDot] â­ ${moved.toFixed(0)} m jitter â€” dot moved, trail unchanged`);
              if (isLive && gMapRef.current && !userAdjustedViewportRef.current) {
                withProgrammaticViewport(() => {
                  gMapRef.current.panTo({ lat, lng });
                });
              }
            }
          } else {
            if (gMapRef.current && !userAdjustedViewportRef.current) {
              withProgrammaticViewport(() => {
                gMapRef.current.panTo({ lat, lng });
              });
            }
          }
        },
        (err) => console.warn("[LiveDot] GPS failed:", err.message),
        { enableHighAccuracy: true, timeout: 20_000, maximumAge: 5_000 }
      );
    };

    updateDot();
    const id = setInterval(updateDot, 10_000);
    return () => clearInterval(id);
  }, [isOwner, isPunchedIn, hasPunchedOut, mapLoaded, routeMode, syncLiveMarker, withProgrammaticViewport]);

  // â”€â”€ Cleanup on unmount â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    return () => {
      liveMarkerClickRef.current?.remove?.();
      liveMarkerClickRef.current = null;
      liveInfoWindowRef.current?.close?.();
      if (liveMarkerAnimationRef.current) {
        cancelAnimationFrame(liveMarkerAnimationRef.current);
        liveMarkerAnimationRef.current = null;
      }
      if (isCurrentlyTracking()) stopTracking();
    };
  }, []);

  // â”€â”€ Socket events â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!socket) return;

    const onAck   = (data) => setSockAck(data.timestamp);
    const onError = (data) => toast.error(data.message || "Socket error.", { title: "Live Sync Error" });

    const onGeofenceAlert = (data) => {
      const msg = `${data.event === "entered" ? "ðŸŸ  Entered" : "âšª Exited"} "${data.name}"`;
      toast[data.event === "entered" ? "warn" : "info"](msg, { title: "Geofence Alert" });
      setGeofenceAlerts((prev) => [{ ...data, id: Date.now() }, ...prev.slice(0, 19)]);
    };

    const onLiveUpdate = (data) => {
      if (data.userId && userId && String(data.userId) !== String(userId)) return;
      if (!data.lat || !data.lng) return;
      const livePoint = {
        lat: data.lat,
        lng: data.lng,
        accuracy: data.accuracy,
        recordedAt: data.recordedAt || data.timestamp || data.time || new Date().toISOString(),
      };
      if (!currentLocationPointRef.current) {
        syncLiveMarker(livePoint);
      }
      const rawMerged = [...rawPtsRef.current];
      appendSequentialPoint(rawMerged, livePoint, 1);
      rawPtsRef.current = rawMerged;

      const last = allPtsRef.current[allPtsRef.current.length - 1];
      if (last) {
        const dist = distMetres(last.lat, last.lng, data.lat, data.lng);
        const minMoveRequired = routeMode === ROUTE_MODE_RAW
          ? 3
          : getEffectiveMoveThreshold(data.accuracy, last.accuracy);
        if (dist < minMoveRequired) {
          console.log(`[Socket] â­ ${dist.toFixed(0)} m jitter â€” skipped`);
          return;
        }
      }
      const merged = [...allPtsRef.current, livePoint];
      const finalTrail = cleanPoints(merged, { preserveDetail: routeMode === ROUTE_MODE_RAW });
      allPtsRef.current = finalTrail;
      drawTrail(finalTrail, { autoFit: false, autoPanLive: false, rawPoints: rawPtsRef.current });
    };

    socket.on("location:ack",         onAck);
    socket.on("location:error",       onError);
    socket.on("location:live_update", onLiveUpdate);
    socket.on("geofence:alert",       onGeofenceAlert);

    return () => {
      socket.off("location:ack",         onAck);
      socket.off("location:error",       onError);
      socket.off("location:live_update", onLiveUpdate);
      socket.off("geofence:alert",       onGeofenceAlert);
    };
  }, [socket, userId, routeMode, syncLiveMarker]);

  useEffect(() => {
    if (!socket || !userId || isOwner) return;
    socket.emit("location:watch_user", { userId });
    console.log(`[AdminMap] ðŸ‘ Watching userId: ${userId}`);
  }, [socket, userId, isOwner]);

  // â”€â”€ Locate Me â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!locateTrigger || !gMapRef.current) return;
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.", { title: "GPS Unavailable" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        gMapRef.current.panTo({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        gMapRef.current.setZoom(17);
      },
      (err) => {
        const msgs = {
          1: "Location permission denied.",
          2: "Current position unavailable.",
          3: "Location request timed out.",
        };
        toast.error(msgs[err.code] || err.message, { title: "Locate Me Failed" });
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15_000 }
    );
  }, [locateTrigger]);

  const accuracyColor =
    accuracy == null ? "#94a3b8"
    : accuracy <= 20  ? "#16a34a"
    : accuracy <= 50  ? "#d97706"
    :                   "#dc2626";

  const accuracyLabel =
    accuracy == null ? "No GPS"
    : accuracy <= 20  ? "Excellent"
    : accuracy <= 50  ? "Fair"
    :                   "Poor";

  const fmtTime = (iso) =>
    iso
      ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      : null;

  const roadTravelKm = Number(roadTravel.totalKm);
  const roadTravelLabel =
    roadTravel.source === "mapbox"
      ? "Mapbox"
      : roadTravel.status === "mapbox_not_configured" || roadTravel.source === "fallback"
        ? "GPS fallback"
        : "Road";

  return (
    <div
      ref={mapOuterContainerRef}
      style={{
        position: "relative",
        width: "100%",
        height: isFullScreen ? "100vh" : height,
        minHeight: isFullScreen ? "100vh" : "380px",
        borderRadius: isFullScreen ? 0 : "inherit",
        background: "#f8fafc",
      }}
    >
      <div ref={mapDivRef} style={{ width: "100%", height: "100%", minHeight: isFullScreen ? "100vh" : "380px", borderRadius: "inherit" }} />

      <button
        type="button"
        onClick={toggleFullScreen}
        title={isFullScreen ? "Exit Fullscreen (Esc)" : "Fullscreen Map"}
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.96)",
          border: "1px solid #cbd5e1",
          borderRadius: "8px",
          padding: "6px 12px",
          fontSize: 12,
          fontWeight: 700,
          color: "#0f172a",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "all 0.2s ease",
        }}
      >
        <span style={{ fontSize: 13 }}>{isFullScreen ? "ðŸ“‰" : "â›¶"}</span>
        <span>{isFullScreen ? "Exit Fullscreen" : "Fullscreen"}</span>
      </button>

      {!mapLoaded && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", alignItems: "center",
          justifyContent: "center", background: "#f8fafc", borderRadius: "inherit", zIndex: 10,
        }}>
          {mapError ? (
            <div style={{ textAlign: "center", maxWidth: 360 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>âš ï¸</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>
                Google Maps Connection Failed
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14, lineHeight: 1.4 }}>
                {mapError}
              </div>
              <button
                type="button"
                onClick={initMap}
                style={{
                  background: "#2563eb", color: "#ffffff", border: 0, borderRadius: 6,
                  padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(37,99,235,0.3)",
                }}
              >
                ðŸ”„ Retry Connection
              </button>
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
            <div style={{
              width: 36, height: 36, border: "3px solid #e2e8f0",
              borderTopColor: "#4569ea", borderRadius: "50%",
              animation: "spin 0.8s linear infinite", margin: "0 auto 12px",
            }} />
            <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>Loading Google Mapsâ€¦</div>
          </div>
          )}
        </div>
      )}

      <div style={{
        position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
        zIndex: 1000, background: "rgba(255,255,255,0.97)", borderRadius: 999,
        padding: "5px 16px", fontSize: 12, fontWeight: 700,
        boxShadow: "0 2px 10px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", gap: 7,
        border: `1px solid ${isLive ? "#bbf7d0" : hasPunchedOut ? "#fde68a" : "#e2e8f0"}`,
        color: isLive ? "#16a34a" : hasPunchedOut ? "#d97706" : "#94a3b8",
        pointerEvents: "none",
      }}>
        <span style={{
          width: 7, height: 7, borderRadius: "50%", display: "inline-block",
          background: isLive ? "#22c55e" : hasPunchedOut ? "#f59e0b" : "#cbd5e1",
          animation: isLive ? "livePulse 1.5s ease-in-out infinite" : "none",
        }} />
        {isLive
          ? `Live Tracking${connected ? "" : " (socket offline)"}`
          : hasPunchedOut
          ? "Day Completed"
          : "Punch in to start tracking"}
      </div>

      {geofenceAlerts.length > 0 && (
        <div style={{
          position: "absolute", top: 50, left: 10, zIndex: 1000,
          maxHeight: 120, overflowY: "auto", display: "flex",
          flexDirection: "column", gap: 4, pointerEvents: "none",
        }}>
          {geofenceAlerts.slice(0, 4).map((a) => (
            <div key={a.id} style={{
              background: "rgba(255,255,255,0.96)", borderRadius: 7,
              padding: "4px 10px", fontSize: 11, fontWeight: 600,
              boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
              color:   a.event === "entered" ? "#c2410c" : "#64748b",
              border: `1px solid ${a.event === "entered" ? "#fed7aa" : "#e2e8f0"}`,
            }}>
              {a.event === "entered" ? "ðŸŸ " : "âšª"} {a.name}
            </div>
          ))}
        </div>
      )}

      {accuracy != null && (
        <div style={{
          position: "absolute", top: 10, right: 10, zIndex: 1000,
          background: "rgba(255,255,255,0.97)", borderRadius: 8,
          padding: "4px 10px", fontSize: 11, fontWeight: 700,
          boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
          color: accuracyColor, border: `1px solid ${accuracyColor}40`, pointerEvents: "none",
        }}>
          GPS Â±{Math.round(accuracy)} m Â· {accuracyLabel}
        </div>
      )}

      <div style={{
        position: "absolute", top: accuracy != null ? 44 : 10, right: 10, zIndex: 1000,
        background: "rgba(255,255,255,0.97)", borderRadius: 8,
        padding: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
        border: "1px solid #e2e8f0", display: "flex", gap: 3,
      }}>
        {[
          { value: ROUTE_MODE_ROAD, label: "Road Route" },
          { value: ROUTE_MODE_RAW, label: "Raw GPS" },
        ].map((mode) => {
          const active = routeMode === mode.value;
          return (
            <button
              key={mode.value}
              type="button"
              onClick={() => setRouteMode(mode.value)}
              style={{
                border: 0,
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                color: active ? "#ffffff" : "#475569",
                background: active ? "#ef4444" : "transparent",
                whiteSpace: "nowrap",
              }}
            >
              {mode.label}
            </button>
          );
        })}
      </div>

      {!isOwner && (
        <div style={{
          position: "absolute", top: accuracy != null ? 84 : 50, right: 10, zIndex: 1000,
          background: "rgba(255,255,255,0.97)", borderRadius: 8,
          padding: 3, boxShadow: "0 1px 6px rgba(0,0,0,0.1)",
          border: "1px solid #e2e8f0", display: "flex", gap: 3, flexWrap: "wrap",
          justifyContent: "flex-end", maxWidth: 230,
        }}>
          {[
            { value: DRAW_MODE_CIRCLE, label: "Circle" },
            { value: DRAW_MODE_POLYGON, label: "Polygon" },
          ].map((mode) => {
            const active = drawMode === mode.value;
            return (
              <button
                key={mode.value}
                type="button"
                onClick={() => setDrawMode((current) => {
                  clearDraftFence();
                  return current === mode.value ? null : mode.value;
                })}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: "5px 8px",
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: "pointer",
                  color: active ? "#ffffff" : "#475569",
                  background: active ? "#f97316" : "transparent",
                  whiteSpace: "nowrap",
                }}
              >
                {mode.label}
              </button>
            );
          })}
          {drawMode === DRAW_MODE_POLYGON && (
            <button
              type="button"
              onClick={finishPolygonGeofence}
              style={{
                border: 0,
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                color: "#ffffff",
                background: "#16a34a",
                whiteSpace: "nowrap",
              }}
            >
              Finish
            </button>
          )}
          {drawMode && (
            <button
              type="button"
              onClick={() => {
                clearDraftFence();
                setDrawMode(null);
              }}
              style={{
                border: 0,
                borderRadius: 6,
                padding: "5px 8px",
                fontSize: 11,
                fontWeight: 800,
                cursor: "pointer",
                color: "#475569",
                background: "transparent",
                whiteSpace: "nowrap",
              }}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={focusCurrentLocation}
        title="Go to live user location"
        aria-label="Go to live user location"
        style={{
          position: "absolute",
          top: !isOwner ? (accuracy != null ? 124 : 90) : (accuracy != null ? 84 : 50),
          right: 10,
          zIndex: 1000,
          border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: 8,
          background: "rgba(255,255,255,0.98)",
          boxShadow: "0 1px 6px rgba(0,0,0,0.12)",
          color: "#b91c1c",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 800,
          padding: "7px 10px",
          whiteSpace: "nowrap",
        }}
      >
        <span style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: "#ef4444",
          boxShadow: "0 0 0 4px rgba(239,68,68,0.16)",
          display: "inline-block",
        }} />
        Live Location
      </button>

      {sockAck && (
        <div style={{
          position: "absolute", bottom: 10, right: 10, zIndex: 1000,
          background: "rgba(255,255,255,0.95)", borderRadius: 6,
          padding: "4px 10px", fontSize: 10, color: "#16a34a",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)", pointerEvents: "none",
        }}>
          âœ“ Saved {fmtTime(sockAck)}
        </div>
      )}

      <div style={{
        position: "absolute", bottom: 10, left: 10, zIndex: 1000,
        background: "rgba(255,255,255,0.97)", borderRadius: 10,
        padding: "8px 12px", fontSize: 12, fontWeight: 700, color: "#0f172a",
        boxShadow: "0 2px 10px rgba(0,0,0,0.12)", pointerEvents: "none",
        display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0",
      }}>
        {routeMode === ROUTE_MODE_RAW ? (
          <>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: "#16a34a",
                border: "2px solid #ffffff",
                boxSizing: "border-box",
              }} />
              GPS points
            </span>
            <span style={{ color: "#e2e8f0" }}>|</span>
            {(gpsDotCount || pointCount) > 0
              ? <span>{gpsDotCount || pointCount} point{(gpsDotCount || pointCount) !== 1 ? "s" : ""}</span>
              : <span style={{ color: "#94a3b8" }}>Waiting for movement...</span>}
          </>
        ) : (
          <>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{
                width: 18,
                height: 0,
                borderTop: "4px solid #ef4444",
                borderRadius: 999,
              }} />
              Route
            </span>
            <span style={{ color: "#e2e8f0" }}>|</span>
            <span style={{ color: "#475569", fontSize: 11 }}>
              Clean road view
            </span>
          </>
        )}
        {roadTravel.loading ? (
          <>
            <span style={{ color: "#e2e8f0" }}>|</span>
            <span style={{ color: "#64748b", fontSize: 11 }}>Checking KM...</span>
          </>
        ) : Number.isFinite(roadTravelKm) && roadTravelKm > 0 ? (
          <>
            <span style={{ color: "#e2e8f0" }}>|</span>
            <span style={{ color: "#0f172a", fontSize: 11, fontWeight: 800 }}>
              {roadTravelLabel} {roadTravelKm.toFixed(2)} km
            </span>
          </>
        ) : null}
        {isLive && (
          <>
            <span style={{ color: "#e2e8f0" }}>|</span>
            <span style={{ color: "#22c55e", fontSize: 11, fontWeight: 700 }}>â— live</span>
          </>
        )}
      </div>

      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.25} }
        @keyframes spin { to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
}





